import {
  Component,
  EventEmitter,
  Output,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DataConnection } from '@nimmsta/ng-commons-lib/models';
import { DataConnectionService } from '@nimmsta/ng-workflow-lib/services/data-connection';
import { TranslatePipe } from '@ngx-translate/core';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import { PrimeTemplate } from 'primeng/api';
import { DatePicker } from 'primeng/datepicker';
import dayjs from 'dayjs';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  JobResultAnalyticsDashboardConfiguration,
  AggregationType,
  AnalyticsMetricType,
  ChartType,
  DateSourceType,
  EmptyBucketMode,
  FilterOperator,
  AggregationField,
  ResultFieldFilter,
  GroupingMode,
  DateGranularity,
  DEFAULT_JOB_RESULT_ANALYTICS_DASHBOARD_RANGE,
  MAX_AGGREGATION_RESULTS,
  MAX_AGGREGATIONS,
  MAX_FILTERS,
  MAX_DATE_RANGE_DAYS,
} from './job-result-analytics-dashboard-configuration';
import {
  JobResultAnalyticsDashboardService,
  FieldInfo,
  JobResultFieldsResponse,
} from './job-result-analytics-dashboard.service';
import { JobDashboardRange } from '../job-dashboard/job-dashboard-configuration';
import {
  DASHBOARD_EDITOR_CONTEXT,
  DashboardEditorContext,
  filterAvailableDataConnectionIds,
  getDeclaredResultFields,
  isWorkflowTemplateDashboard,
} from '../../../../models/dashboard';

@Component({
  selector: 'app-job-result-analytics-dashboard-form',
  templateUrl: './job-result-analytics-dashboard-form.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    MultiSelect,
    Select,
    PrimeTemplate,
    DatePicker,
  ],
})
export class JobResultAnalyticsDashboardFormComponent
  implements OnInit, OnDestroy
{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data = inject<JobResultAnalyticsDashboardConfiguration>('formData' as any);
  private fb = inject(FormBuilder);
  private dataConnectionService = inject(DataConnectionService);
  private analyticsService = inject(JobResultAnalyticsDashboardService);
  private editorContext = inject<DashboardEditorContext | null>(
    DASHBOARD_EDITOR_CONTEXT,
    { optional: true },
  );
  private readonly destroy$ = new Subject<void>();
  private readonly discoverFieldsTrigger$ = new Subject<{
    dataConnectionIds: string[];
    forceRefresh: boolean;
  }>();

  form!: FormGroup;

  @Output() dataChange =
    new EventEmitter<JobResultAnalyticsDashboardConfiguration>();

  dataConnections: DataConnection[] = [];
  availableFields: FieldInfo[] = [];
  isLoadingFields = false;
  isTemplateDashboard = isWorkflowTemplateDashboard(this.editorContext);
  readonly browserTimezone = this.getBrowserTimezone();

  customRange = JobDashboardRange.CUSTOM;
  dateRange: Date[] = [];
  dateRangeError = '';
  AnalyticsMetricType = AnalyticsMetricType;
  DateSourceType = DateSourceType;
  GroupingMode = GroupingMode;

  // Filter out ranges that could exceed MAX_DATE_RANGE_DAYS (90 days)
  private readonly excludedRanges = new Set([
    JobDashboardRange.LAST_365_DAYS,
    JobDashboardRange.ALL_TIME,
    JobDashboardRange.THIS_QUARTER,
    JobDashboardRange.THIS_YEAR,
  ]);

  rangeOptions = Object.entries(JobDashboardRange)
    .filter(([, value]) => !this.excludedRanges.has(value as JobDashboardRange))
    .map(([key, value]) => ({
      key,
      value: value as JobDashboardRange,
    }));

  maxAggregations = MAX_AGGREGATIONS;
  maxFilters = MAX_FILTERS;
  maxDateRangeDays = MAX_DATE_RANGE_DAYS;

  groupingModeOptions = Object.entries(GroupingMode).map(([key, value]) => ({
    key,
    value: value as GroupingMode,
  }));

  dateGranularityOptions = Object.entries(DateGranularity).map(
    ([key, value]) => ({
      key,
      value: value as DateGranularity,
    }),
  );

  metricTypeOptions = Object.entries(AnalyticsMetricType).map(
    ([key, value]) => ({
      key,
      value: value as AnalyticsMetricType,
    }),
  );

  dateSourceTypeOptions = Object.entries(DateSourceType).map(
    ([key, value]) => ({
      key,
      value: value as DateSourceType,
    }),
  );

  emptyBucketModeOptions = Object.entries(EmptyBucketMode).map(
    ([key, value]) => ({
      key,
      value: value as EmptyBucketMode,
    }),
  );

  aggregationTypes = Object.entries(AggregationType).map(([key, value]) => ({
    key,
    value: value as AggregationType,
  }));

  chartTypes = Object.entries(ChartType).map(([key, value]) => ({
    key,
    value: value as ChartType,
  }));

  filterOperators = Object.entries(FilterOperator).map(([key, value]) => ({
    key,
    value: value as FilterOperator,
  }));

  statusOptions = [
    { key: 'OPEN', value: 'OPEN' },
    { key: 'IN_PROGRESS', value: 'IN_PROGRESS' },
    { key: 'COMPLETE', value: 'COMPLETE' },
    { key: 'CANCELLED', value: 'CANCELLED' },
  ];

  maxLimit = MAX_AGGREGATION_RESULTS;

  ngOnInit() {
    if (this.isTemplateDashboard) {
      this.availableFields = getDeclaredResultFields(this.editorContext);
      this.form.patchValue({ dataConnectionIds: [] });
      return;
    }
    this.loadDataConnections();

    this.discoverFieldsTrigger$
      .pipe(
        tap(() => {
          this.isLoadingFields = true;
        }),
        switchMap(({ dataConnectionIds, forceRefresh }) =>
          this.analyticsService
            .discoverFields(dataConnectionIds, forceRefresh)
            .pipe(
              catchError(() =>
                of({ fields: [], sampleSize: 0 } as JobResultFieldsResponse),
              ),
            ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.availableFields = response.fields;
        this.enforceNumericAggregationCompatibility();
        this.isLoadingFields = false;
      });

    this.form
      .get('dataConnectionIds')
      ?.valueChanges.pipe(
        startWith(this.form.get('dataConnectionIds')?.value),
        map((value) => this.normalizeDataConnectionIds(value)),
        debounceTime(250),
        distinctUntilChanged((prev, curr) => this.haveSameEntries(prev, curr)),
        takeUntil(this.destroy$),
      )
      .subscribe((dataConnectionIds) => {
        this.requestFieldDiscovery(dataConnectionIds, false);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor() {
    const data = this.data;
    const initialGroupingMode = this.getInitialGroupingMode(data);
    const initialDateGranularity =
      data?.dateGroupBy?.granularity || DateGranularity.DAY;
    const initialMetricType =
      data?.metricType ||
      (data?.bucketedAverage
        ? AnalyticsMetricType.BUCKETED_AVERAGE
        : AnalyticsMetricType.AGGREGATE);

    this.form = this.fb.group({
      title: [data?.title || ''],
      description: [data?.description || ''],
      dataConnectionIds: [data?.dataConnectionIds || []],
      statuses: [data?.statuses || []],
      range: [data?.range || DEFAULT_JOB_RESULT_ANALYTICS_DASHBOARD_RANGE],
      from: [data?.from || null],
      to: [data?.to || null],
      metricType: [initialMetricType],
      aggregations: this.fb.array(
        data?.aggregations?.map((a) => this.createAggregationGroup(a)) || [],
      ),
      filters: this.fb.array(
        data?.filters?.map((f) => this.createFilterGroup(f)) || [],
      ),
      groupingMode: [initialGroupingMode],
      groupBy: [data?.groupBy || null],
      dateGroupByGranularity: [initialDateGranularity],
      bucketGranularity: [
        data?.bucketedAverage?.bucket?.granularity || DateGranularity.DAY,
      ],
      dateSourceType: [
        data?.bucketedAverage?.dateSource?.type ||
          DateSourceType.JOB_LAST_UPDATED,
      ],
      dateSourceFieldKey: [data?.bucketedAverage?.dateSource?.fieldKey || null],
      bucketValueFieldKey: [data?.bucketedAverage?.value?.fieldKey || ''],
      emptyBucketMode: [
        data?.bucketedAverage?.emptyBucketMode ||
          EmptyBucketMode.INCLUDE_AS_ZERO,
      ],
      chartType: [data?.chartType || ChartType.TABLE],
      limit: [data?.limit || null],
    });

    if (data?.from && data?.to) {
      this.dateRange = [dayjs(data.from).toDate(), dayjs(data.to).toDate()];
    }

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.emitConfiguration();
    });
    this.form
      .get('metricType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateBucketedAverageValidators());
    this.form
      .get('dateSourceType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateBucketedAverageValidators());

    this.updateBucketedAverageValidators();
    this.emitConfiguration();
  }

  private createAggregationGroup(agg?: AggregationField) {
    return this.fb.group({
      fieldKey: [agg?.fieldKey || '', Validators.required],
      type: [agg?.type || AggregationType.SUM, Validators.required],
    });
  }

  private createFilterGroup(filter?: ResultFieldFilter) {
    return this.fb.group({
      fieldKey: [filter?.fieldKey || '', Validators.required],
      operator: [
        filter?.operator || FilterOperator.EQUALS,
        Validators.required,
      ],
      value: [filter?.value || '', Validators.required],
    });
  }

  get aggregations(): FormArray {
    return this.form.get('aggregations') as FormArray;
  }

  get filters(): FormArray {
    return this.form.get('filters') as FormArray;
  }

  addAggregation() {
    const index = this.aggregations.length;
    this.aggregations.push(this.createAggregationGroup());
    this.ensureCompatibleAggregationTypeForRow(index);
  }

  removeAggregation(index: number) {
    this.aggregations.removeAt(index);
  }

  addFilter() {
    this.filters.push(this.createFilterGroup());
  }

  removeFilter(index: number) {
    this.filters.removeAt(index);
  }

  private loadDataConnections() {
    this.dataConnectionService
      .simpleList()
      .subscribe((connections: DataConnection[]) => {
        connections.sort((a, b) => a.name.localeCompare(b.name));
        this.dataConnections = connections;
        this.removeUnavailableDataConnectionIds(connections);
      });
  }

  discoverFields() {
    if (this.isTemplateDashboard) {
      return;
    }
    const dataConnectionIds = this.normalizeDataConnectionIds(
      this.form?.get('dataConnectionIds')?.value,
    );
    this.requestFieldDiscovery(dataConnectionIds, true);
  }

  private requestFieldDiscovery(
    dataConnectionIds: string[],
    forceRefresh: boolean,
  ) {
    this.discoverFieldsTrigger$.next({ dataConnectionIds, forceRefresh });
  }

  private normalizeDataConnectionIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return [...new Set(value)]
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
      .sort();
  }

  private haveSameEntries(a: string[], b: string[]): boolean {
    return (
      a.length === b.length && a.every((value, index) => value === b[index])
    );
  }

  private removeUnavailableDataConnectionIds(connections: DataConnection[]) {
    this.form.patchValue({
      dataConnectionIds: filterAvailableDataConnectionIds(
        this.form.get('dataConnectionIds')?.value,
        connections.map((connection) => connection.id),
      ),
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customRangeChange(event: any) {
    const from = event[0] ? dayjs(event[0]) : null;
    const to = event[1] ? dayjs(event[1]) : null;

    // Validate date range doesn't exceed maximum
    if (from && to) {
      const daysDiff = to.diff(from, 'day');
      if (daysDiff > MAX_DATE_RANGE_DAYS) {
        this.dateRangeError = `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days (selected: ${daysDiff} days)`;
        return;
      }
    }
    this.dateRangeError = '';

    this.form.patchValue({
      range: this.customRange,
      from,
      to,
    });
  }

  getFieldOptions() {
    return this.availableFields.map((f) => ({
      key: f.key,
      value: f.key,
      label: `${f.key} (${f.inferredType})`,
    }));
  }

  getAggregationTypeOptionsForRow(index: number) {
    const row = this.aggregations.at(index) as FormGroup | null;
    const fieldKey = row?.get('fieldKey')?.value;
    if (this.isKnownNonNumericField(fieldKey)) {
      return this.aggregationTypes.filter(
        (option) => option.value === AggregationType.COUNT,
      );
    }
    return this.aggregationTypes;
  }

  onAggregationFieldChange(index: number) {
    this.ensureCompatibleAggregationTypeForRow(index);
  }

  onAggregationTypeChange(index: number) {
    this.ensureCompatibleAggregationTypeForRow(index);
  }

  onMetricTypeChange() {
    if (
      this.form.get('metricType')?.value !==
      AnalyticsMetricType.BUCKETED_AVERAGE
    ) {
      return;
    }

    const statuses = this.form.get('statuses')?.value;
    if (!Array.isArray(statuses) || statuses.length === 0) {
      this.form.patchValue({ statuses: ['COMPLETE'] });
    }
  }

  isBucketedAverageControlInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  private enforceNumericAggregationCompatibility() {
    this.aggregations.controls.forEach((_, index) =>
      this.ensureCompatibleAggregationTypeForRow(index),
    );
  }

  private ensureCompatibleAggregationTypeForRow(index: number) {
    const row = this.aggregations.at(index) as FormGroup | null;
    if (!row) {
      return;
    }

    const fieldKey = row.get('fieldKey')?.value;
    if (!this.isKnownNonNumericField(fieldKey)) {
      return;
    }

    const typeControl = row.get('type');
    if (!typeControl || typeControl.value === AggregationType.COUNT) {
      return;
    }

    typeControl.patchValue(AggregationType.COUNT);
  }

  private isKnownNonNumericField(fieldKey: unknown): boolean {
    if (typeof fieldKey !== 'string' || !fieldKey) {
      return false;
    }

    const field = this.availableFields.find((f) => f.key === fieldKey);
    return !!field && field.inferredType !== 'number';
  }

  private updateBucketedAverageValidators() {
    const isBucketedAverage =
      this.form.get('metricType')?.value ===
      AnalyticsMetricType.BUCKETED_AVERAGE;
    const usesResultDateSource =
      this.form.get('dateSourceType')?.value === DateSourceType.RESULT_FIELD;
    const valueFieldControl = this.form.get('bucketValueFieldKey');
    const dateSourceFieldControl = this.form.get('dateSourceFieldKey');

    const nonBlankValidators = [Validators.required, Validators.pattern(/\S/)];
    valueFieldControl?.setValidators(
      isBucketedAverage ? nonBlankValidators : [],
    );
    dateSourceFieldControl?.setValidators(
      isBucketedAverage && usesResultDateSource ? nonBlankValidators : [],
    );
    valueFieldControl?.updateValueAndValidity({ emitEvent: false });
    dateSourceFieldControl?.updateValueAndValidity({ emitEvent: false });
  }

  private getInitialGroupingMode(
    data?: JobResultAnalyticsDashboardConfiguration,
  ): GroupingMode {
    if (data?.dateGroupBy) {
      return GroupingMode.DATE;
    }
    if (data?.groupBy) {
      return GroupingMode.FIELD;
    }
    return GroupingMode.NONE;
  }

  private emitConfiguration() {
    const value = this.form.getRawValue();
    const groupingMode = value.groupingMode as GroupingMode;
    const metricType = value.metricType as AnalyticsMetricType;

    const config: JobResultAnalyticsDashboardConfiguration = {
      title: value.title,
      description: value.description,
      dataConnectionIds: value.dataConnectionIds,
      statuses: value.statuses,
      range: value.range,
      from: value.from,
      to: value.to,
      metricType,
      aggregations: value.aggregations,
      filters: value.filters,
      groupingMode,
      chartType: value.chartType,
      limit: value.limit,
    };

    if (metricType === AnalyticsMetricType.BUCKETED_AVERAGE) {
      config.bucketedAverage = {
        bucket: {
          granularity:
            (value.bucketGranularity as DateGranularity) || DateGranularity.DAY,
        },
        timezone: this.browserTimezone,
        dateSource: {
          type:
            (value.dateSourceType as DateSourceType) ||
            DateSourceType.JOB_LAST_UPDATED,
          fieldKey:
            value.dateSourceType === DateSourceType.RESULT_FIELD
              ? value.dateSourceFieldKey?.trim()
              : undefined,
        },
        value: {
          fieldKey: value.bucketValueFieldKey?.trim(),
          aggregation: AggregationType.COUNT_DISTINCT,
        },
        emptyBucketMode:
          (value.emptyBucketMode as EmptyBucketMode) ||
          EmptyBucketMode.INCLUDE_AS_ZERO,
      };
      this.dataChange.emit(config);
      return;
    }

    if (groupingMode === GroupingMode.FIELD && value.groupBy) {
      config.groupBy = value.groupBy;
    }

    if (groupingMode === GroupingMode.DATE) {
      config.dateGroupBy = this.getDateGroupByFromForm(
        value.dateGroupByGranularity,
      );
    }

    this.dataChange.emit(config);
  }

  private getDateGroupByFromForm(granularityValue: unknown) {
    const granularity =
      (granularityValue as DateGranularity) || DateGranularity.DAY;
    return { granularity, timezone: this.browserTimezone };
  }

  private getBrowserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }
}
